import { Popover, PopoverActions } from '@material-ui/core';
import clsx from 'clsx';
import { Body } from 'copilot-design-system';
import { useContext, useEffect, useRef, useState } from 'react';

import { FlagsContext } from 'src/context';
import { AddressInputForm } from 'src/features/crm/properties/AddressInput/AddressInputForm';
import { CustomFieldValueData } from 'src/features/crm/types/type';
import BaseTypography from 'src/legacy/components/Text/BaseTypography';
import { EditableCellValueWrapper } from 'src/legacy/components/UI/Tables/EditableCellWrapper';
import {
  AddressCustomField,
  AddressCustomFieldValues,
} from 'src/store/clients/types';
import { LightGray } from 'src/theme/colors';

interface AddressInputProps {
  field: AddressCustomField;
  value: AddressCustomFieldValues;
  placeholder: string;
  onSaveValue: (property: CustomFieldValueData) => void;
  onClose?: () => void;
  renderAsCell?: boolean;
  shouldOpenMenu?: boolean;
}

const AddressInput = ({
  field,
  value,
  placeholder,
  onSaveValue,
  onClose,
  renderAsCell,
  shouldOpenMenu,
}: AddressInputProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const popoverActions = useRef<PopoverActions>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { EnableCrmRightSidebarRedesign } = useContext(FlagsContext);

  // open the popover when shouldOpenMenu is set to true
  useEffect(() => {
    if (shouldOpenMenu && containerRef !== null) {
      setAnchorEl(containerRef.current);
    }
  }, [shouldOpenMenu]);

  return (
    <>
      <div
        ref={containerRef}
        className={clsx('w-full flex items-center cursor-pointer pt-1', {
          ' min-h-8 ': EnableCrmRightSidebarRedesign,
          'h-full': !EnableCrmRightSidebarRedesign,
        })}
        onClickCapture={(e) => {
          e.stopPropagation();
          setAnchorEl(e.currentTarget);
        }}
      >
        {EnableCrmRightSidebarRedesign ? (
          renderAsCell ? (
            <Body size="sm">{value.fullAddress}</Body>
          ) : (
            <>
              {!value.fullAddress ? (
                <Body size="sm" className="text-light-gray">
                  {placeholder}
                </Body>
              ) : (
                <Body className="whitespace-pre-wrap" size="sm">
                  {value.fullAddress}
                </Body>
              )}
            </>
          )
        ) : renderAsCell ? (
          <EditableCellValueWrapper>
            <BaseTypography>{value.fullAddress}</BaseTypography>
          </EditableCellValueWrapper>
        ) : (
          <>
            {!value.fullAddress ? (
              <BaseTypography fontType="13Medium" style={{ color: LightGray }}>
                {placeholder}
              </BaseTypography>
            ) : (
              <BaseTypography
                className="whitespace-pre-wrap"
                fontType="13Medium"
              >
                {value.fullAddress}
              </BaseTypography>
            )}
          </>
        )}
      </div>
      <Popover
        onClose={() => {
          if (onClose) onClose();
          setAnchorEl(null);
        }}
        open={anchorEl !== null}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        action={popoverActions}
        classes={{
          paper:
            'border border-solid border-secondary-hover shadow-dropdown-menu w-[263px]',
        }}
      >
        <AddressInputForm
          fieldId={field.id}
          initialValues={{
            addressLine1: value.addressLine1 ?? '',
            addressLine2: value.addressLine2 ?? '',
            city: value.city ?? '',
            country: value.country ?? '',
            fullAddress: value.fullAddress ?? '',
            region: value.region ?? '',
            postalCode: value.postalCode ?? '',
          }}
          onClosePopover={() => {
            setAnchorEl(null);
          }}
          onUpdatePopoverPosition={() => {
            if (popoverActions.current) {
              popoverActions.current.updatePosition();
            }
          }}
          onSaveValue={onSaveValue}
        />
      </Popover>
    </>
  );
};

export default AddressInput;
